import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { API_URI } from 'config'
import {
    AUTH_USE_HTTPONLY_COOKIE,
    clearAuthStorage,
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
} from './authStorage'

/** True when the JWT access token is missing, malformed, or past its exp claim. */
export function isAccessTokenExpired(token) {
    if (!token) return true
    try {
        const { exp } = jwtDecode(token)
        if (!exp) return false
        return Date.now() >= exp * 1000
    } catch {
        return true
    }
}

const REFRESH_URL = `${API_URI}/api/admin/auth/refresh`

let refreshPromise = null

export function applyAccessTokenToAxios(accessToken, axiosInstance = axios) {
    if (accessToken) {
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    } else {
        delete axiosInstance.defaults.headers.common.Authorization
    }
}

export function persistAuthTokens({ accessToken, refreshToken }) {
    if (accessToken) {
        setAccessToken(accessToken)
        applyAccessTokenToAxios(accessToken)
    }
    if (refreshToken && !AUTH_USE_HTTPONLY_COOKIE) {
        setRefreshToken(refreshToken)
    }
}

export async function refreshAccessToken() {
    if (refreshPromise) {
        return refreshPromise
    }

    refreshPromise = (async () => {
        const body = {}
        const storedRefresh = getRefreshToken()
        if (storedRefresh) {
            body.refreshToken = storedRefresh
        }

        const { data } = await axios.post(REFRESH_URL, body, {
            withCredentials: true,
        })

        const accessToken = data.accessToken || data.token
        persistAuthTokens({
            accessToken,
            refreshToken: data.refreshToken,
        })

        return accessToken
    })()
        .catch((error) => {
            clearAuthStorage()
            applyAccessTokenToAxios(null)
            throw error
        })
        .finally(() => {
            refreshPromise = null
        })

    return refreshPromise
}

export function isRefreshRequest(config) {
    const url = config?.url || ''
    return url.includes('/admin/auth/refresh')
}

export function isAuthExemptRequest(config) {
    const url = config?.url || ''
    return (
        url.includes('/admin/auth/login') ||
        url.includes('/admin/auth/verify-otp') ||
        url.includes('/admin/auth/resend-otp') ||
        url.includes('/admin/auth/refresh') ||
        url.includes('/admin/auth/login-as-sa') ||
        url.includes('/admin/request-forgot-password') ||
        url.includes('/admin/validate-otp') ||
        url.includes('/admin/change-password/forgot')
    )
}

/**
 * Attach refresh-on-401 behavior to an axios instance.
 */
export function attachAuthRefreshInterceptor(axiosInstance, { onAuthFailure } = {}) {
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config
            const status = error.response?.status

            if (
                status !== 401 ||
                !originalRequest ||
                originalRequest._retry ||
                isRefreshRequest(originalRequest) ||
                isAuthExemptRequest(originalRequest)
            ) {
                return Promise.reject(error)
            }

            const refreshToken = getRefreshToken()
            const canRefresh =
                AUTH_USE_HTTPONLY_COOKIE || Boolean(refreshToken)

            if (!canRefresh) {
                if (onAuthFailure) onAuthFailure()
                return Promise.reject(error)
            }

            originalRequest._retry = true

            try {
                const accessToken = await refreshAccessToken()
                originalRequest.headers = originalRequest.headers || {}
                originalRequest.headers.Authorization = `Bearer ${accessToken}`
                return axiosInstance(originalRequest)
            } catch (refreshError) {
                if (onAuthFailure) onAuthFailure()
                return Promise.reject(refreshError)
            }
        }
    )
}
